import React from 'react'
import {
  Box,
  Badge,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Flex,
  Image
} from '@chakra-ui/react'
import { useNavigate, Link } from 'react-router-dom'
import { FiArrowRight, FiCheckCircle, FiTrendingUp, FiUsers, FiBarChart2, FiGlobe } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { HeroCarousel } from '../components/HeroCarousel'

export const SpotlightLearnMorePage: React.FC = () => {
  const navigate = useNavigate()

  const handleApply = () => navigate('/for-services/onboarding/basic-setup')
  const handleViewSample = () => navigate('/samples')
  const handleHowItWorks = () => navigate('/how-it-works')

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />

      <Box flex={1}>
        {/* Hero */}
        <Box
          bgGradient="linear(to-br, white, blue.50)"
          py={{ base: 20, md: 32 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background decorations */}
          <Box
            position="absolute"
            top="-10%"
            right="-5%"
            w="800px"
            h="800px"
            borderRadius="full"
            bgGradient="radial(brand.100, transparent)"
            opacity={0.4}
            filter="blur(80px)"
          />
          <Box
            position="absolute"
            bottom="-10%"
            left="-5%"
            w="600px"
            h="600px"
            borderRadius="full"
            bgGradient="radial(blue.100, transparent)"
            opacity={0.4}
            filter="blur(80px)"
          />

          <Container maxW="7xl" position="relative" zIndex={1} px={{ base: 4, md: 6, lg: 8 }}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 12, lg: 20 }} alignItems="center">
              <VStack align="start" gap={8}>
                <Badge 
                  colorPalette="brand" 
                  size="lg" 
                  px={4} 
                  py={2} 
                  borderRadius="full" 
                  variant="subtle"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  Livewello Spotlight
                </Badge>
                <Heading 
                  size={{ base: '3xl', md: '4xl', lg: '5xl' }} 
                  color="gray.900" 
                  lineHeight="1.1"
                  fontWeight="extrabold"
                  letterSpacing="tight"
                >
                  Get clients without becoming a marketer.
                </Heading>
                <Text fontSize="xl" color="gray.600" lineHeight="tall">
                  We handle the ads, platforms, and optimization. You focus on your clients. No marketing courses, no ad managers to learn—just results.
                </Text>
                
                <HStack gap={4} flexWrap="wrap" pt={4}>
                  <Button
                    colorPalette="brand"
                    size="xl"
                    fontWeight="bold"
                    px={10}
                    h={16}
                    onClick={handleApply}
                    _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                    transition="all 0.2s"
                  >
                    <HStack gap={2}>
                      <Text>Start Getting Clients</Text>
                      <Icon as={FiArrowRight} />
                    </HStack>
                  </Button>
                  <Button
                    variant="ghost"
                    size="xl"
                    fontWeight="semibold"
                    px={8}
                    h={16}
                    onClick={handleHowItWorks}
                    color="gray.600"
                  >
                    How It Works
                  </Button>
                </HStack>

                <SimpleGrid columns={2} gap={6} pt={4} w="full">
                  <HStack>
                    <Icon as={FiCheckCircle} color="brand.500" />
                    <Text color="gray.600" fontWeight="medium">No ad platforms</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheckCircle} color="brand.500" />
                    <Text color="gray.600" fontWeight="medium">No funnels</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheckCircle} color="brand.500" />
                    <Text color="gray.600" fontWeight="medium">Done-for-you</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCheckCircle} color="brand.500" />
                    <Text color="gray.600" fontWeight="medium">Results-driven</Text>
                  </HStack>
                </SimpleGrid>
              </VStack>

              {/* Hero Visual - Interactive Step Carousel (Desktop) */}
              <Box position="relative" display={{ base: 'none', lg: 'block' }}>
                <HeroCarousel />
                
                {/* Decorative Elements */}
                <Box 
                  position="absolute" 
                  top="-20px" 
                  right="-20px" 
                  w="100px" 
                  h="100px" 
                  bg="brand.400" 
                  borderRadius="full" 
                  opacity={0.1} 
                  zIndex={-1}
                />
                <Box 
                  position="absolute" 
                  bottom="-40px" 
                  left="-40px" 
                  w="150px" 
                  h="150px" 
                  bg="blue.400" 
                  borderRadius="full" 
                  opacity={0.1} 
                  zIndex={-1}
                />
              </Box>
            </SimpleGrid>
            
            {/* Hero Visual - Interactive Step Carousel (Mobile/Tablet) */}
            <Box display={{ base: 'block', lg: 'none' }} mt={10}>
              <HeroCarousel />
            </Box>
          </Container>
        </Box>

        {/* Value Props */}
        <Box bg="white" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
              {[
                {
                  icon: FiGlobe,
                  title: "A page that turns visitors into inquiries",
                  description: "A high-converting Spotlight page that showcases your expertise and acts as your primary landing page."
                },
                {
                  icon: FiUsers,
                  title: "Ads that tell your story and attract the right clients",
                  description: "We generate carousel ads from your answers, then run them on Meta when you activate a pack. Story-first format designed to convert."
                },
                {
                  icon: FiTrendingUp,
                  title: "A steady stream of new clients, not feast or famine",
                  description: "Choose your visibility level and scale up as you need more clients. No more feast or famine."
                }
              ].map((item, idx) => (
                <Card.Root 
                  key={idx} 
                  borderWidth="1px" 
                  borderColor="gray.200" 
                  bg="white" 
                  borderRadius="xl" 
                  shadow="sm"
                  _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  <Card.Body p={8}>
                    <Box p={3} bg="brand.50" borderRadius="lg" w="fit-content" color="brand.600" mb={6}>
                      <Icon as={item.icon} boxSize={6} />
                    </Box>
                    <Heading size="md" color="gray.900" mb={3}>{item.title}</Heading>
                    <Text color="gray.600" lineHeight="tall">
                      {item.description}
                    </Text>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* Feature Strip */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={16} alignItems="center">
              <VStack align="start" gap={8}>
                <Heading size="2xl" color="gray.900">
                  Everything included
                </Heading>
                <VStack align="start" gap={4} w="full">
                  {[
                    "High-converting Spotlight landing page",
                    "Professional ad copy & creative design",
                    "Ad campaign management (Meta)",
                    "Real-time analytics dashboard",
                    "Lead capture & notification system"
                  ].map((feature, i) => (
                    <HStack key={i} w="full" p={4} bg="white" borderRadius="lg" shadow="sm">
                      <Icon as={FiCheckCircle} color="brand.500" boxSize={5} />
                      <Text fontWeight="medium" color="gray.700">{feature}</Text>
                    </HStack>
                  ))}
                </VStack>
                <Button 
                  as={Link}
                  to="/pricing"
                  size="lg" 
                  colorPalette="brand" 
                  variant="outline" 
                  mt={4}
                >
                  See Pricing & Options
                </Button>
              </VStack>
              
              <Box position="relative">
                <Card.Root
                  bg="teal.800"
                  color="white"
                  borderRadius="2xl"
                  overflow="hidden"
                  shadow="2xl"
                >
                  <Card.Body p={10}>
                    <VStack align="start" gap={6}>
                      <Badge colorPalette="brand" variant="solid">Sample Result</Badge>
                      <Heading size="xl">"Within 2 weeks, I had 5 new consultation calls booked."</Heading>
                      <Text fontSize="lg" color="gray.300">
                        "I used to spend hours trying to make Instagram reels. Spotlight generated my carousel ads instantly and took all the marketing off my plate so I could focus on coaching."
                      </Text>
                      <HStack gap={4} pt={4}>
                        <Box w={12} h={12} bg="brand.500" borderRadius="full" />
                        <VStack align="start" gap={0}>
                          <Text fontWeight="bold">Sarah J.</Text>
                          <Text fontSize="sm" color="gray.400">Functional Nutritionist</Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </Box>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Trust Note */}
        <Box bg="gray.50" py={{ base: 8, md: 12 }}>
          <Container maxW="4xl">
            <VStack gap={4} textAlign="center">
              <HStack justify="center" gap={6} flexWrap="wrap">
                <HStack gap={2}>
                  <Icon as={FiCheckCircle} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Verified Pros:</Text> Practitioners verified for expertise and client outcomes
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={FiCheckCircle} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Privacy-Safe:</Text> All metrics aggregated, no client names shared
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={FiCheckCircle} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Spotlight-Only:</Text> All ad traffic goes to Spotlight-managed landing pages
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* Bottom CTA */}
        <Box bg="teal.700" color="white" py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
           <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bgGradient="linear(to-br, teal.700, teal.800)"
            opacity={0.9}
            zIndex={0}
          />
          <Container maxW="4xl" position="relative" zIndex={1}>
            <VStack gap={8} textAlign="center">
              <Heading size={{ base: '3xl', md: '4xl' }} letterSpacing="tight">
                Ready to grow your practice?
              </Heading>
              <Text fontSize="xl" color="teal.100" maxW="2xl" lineHeight="tall">
                No marketing courses, no ad managers to learn—just results.
              </Text>
              <HStack gap={4} justify="center">
                <Button
                  size="xl"
                  bg="white"
                  color="teal.700"
                  fontWeight="bold"
                  px={12}
                  py={8}
                  fontSize="xl"
                  onClick={handleApply}
                  _hover={{ transform: 'scale(1.05)', shadow: '2xl', bg: 'gray.100' }}
                  transition="all 0.2s"
                >
                  <HStack gap={3}>
                    <Text>Start Getting Clients</Text>
                    <Icon as={FiArrowRight} />
                  </HStack>
                </Button>
              </HStack>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          w="full" 
          colorPalette="brand" 
          size="lg" 
          onClick={handleApply}
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

export default SpotlightLearnMorePage
